import { lazy, type ReactNode } from "react";
import { I18nextProvider } from "react-i18next";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../global.scss";

import i18n from "@app/i18n";
import { toastOptions } from "@constants/constants";
import { LanguageProvider } from "@context/language-context";
import { ThemeProvider } from "@context/theme-context";
import { DndContext } from "@dnd-kit/core";
import { AuthProvider } from "@features/auth/auth";
import * as Tooltip from "@radix-ui/react-tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
	createRootRoute,
	HeadContent,
	Navigate,
	Outlet,
	Scripts,
} from "@tanstack/react-router";
import { ErrorComponent } from "@ui/error/error";
import { tokenInstance } from "@utils/token-instance";

const Devtools =
	process.env.NODE_ENV === "production"
		? () => null // Render nothing in production
		: lazy(async () => {
				// Lazy load in development: unified TanStack Devtools shell
				// with the Router and Query panels plugged in.
				// Cmd+Shift+D → panel; hold Cmd+Shift + click → open source in editor.
				//
				// The devtools persists its settings in localStorage after first run,
				// which silently overrides the config below on every later visit.
				// Sync the code-owned keys into storage so the hotkeys always match.
				try {
					const KEY = "tanstack_devtools_settings";
					const stored = JSON.parse(localStorage.getItem(KEY) ?? "{}");
					localStorage.setItem(
						KEY,
						JSON.stringify({
							...stored,
							openHotkey: ["CtrlOrMeta", "Shift", "D"],
							inspectHotkey: ["CtrlOrMeta", "Shift"],
							sourceAction: "ide-warp",
						}),
					);
				} catch {
					// storage unavailable — devtools falls back to its defaults
				}
				const [devtools, routerPanel, queryPanel] = await Promise.all([
					import("@tanstack/react-devtools"),
					import("@tanstack/react-router-devtools"),
					import("@tanstack/react-query-devtools"),
				]);
				const { TanStackDevtools } = devtools;
				const { TanStackRouterDevtoolsPanel } = routerPanel;
				const { ReactQueryDevtoolsPanel } = queryPanel;
				return {
					default: () => (
						<TanStackDevtools
							config={{
								// Cmd+Shift+D → toggle the devtools panel
								openHotkey: ["CtrlOrMeta", "Shift", "D"],
								// Hold Cmd+Shift → element inspector; click → open source in editor
								inspectHotkey: ["CtrlOrMeta", "Shift"],
								sourceAction: "ide-warp",
							}}
							plugins={[
								{
									name: "TanStack Router",
									render: <TanStackRouterDevtoolsPanel />,
								},
								{
									name: "TanStack Query",
									render: <ReactQueryDevtoolsPanel />,
								},
							]}
						/>
					),
				};
			});

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			retry: false,
		},
	},
});

function RootDocument({ children }: { children: ReactNode }) {
	return (
		<html lang="en" translate="no">
			<head>
				<HeadContent />
			</head>
			<body>
				<div id="root">{children}</div>
				<Scripts />
			</body>
		</html>
	);
}

export const Route = createRootRoute({
	head: () => ({
		meta: [
			{ charSet: "UTF-8" },
			{ name: "viewport", content: "width=device-width, initial-scale=1.0" },
			{ name: "google", content: "notranslate" },
			{ name: "google-translate-customization", content: "disabled" },
			{ title: "zAdmin" },
		],
		links: [{ rel: "icon", type: "image/svg+xml", href: "/favicon.svg" }],
		scripts: [
			{ src: "./env-config.js" },
			{
				children:
					"window.fw = window.fw || function () {(window.fw.q = window.fw.q || []).push(arguments);};",
			},
			{
				async: true,
				src: "https://cdn.jsdelivr.net/npm/@zyplai/support-widget@0.1.8/dist/widget.iife.js",
			},
		],
	}),
	shellComponent: RootDocument,
	component: () => (
		<QueryClientProvider client={queryClient}>
			<AuthProvider>
				<ThemeProvider>
					<LanguageProvider>
						<I18nextProvider i18n={i18n}>
							<Tooltip.Provider delayDuration={200}>
								<DndContext>
									<Outlet />
									<ToastContainer {...toastOptions} />
									<Devtools />
								</DndContext>
							</Tooltip.Provider>
						</I18nextProvider>
					</LanguageProvider>
				</ThemeProvider>
			</AuthProvider>
		</QueryClientProvider>
	),
	errorComponent: (error) => ErrorComponent(error),
	notFoundComponent: () => {
		const token = tokenInstance.getToken();
		return <Navigate to={token ? "/applications" : "/login"} replace />;
	},
});
